import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Send } from "lucide-react";

export default function AuthPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      navigate("/");
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-indigo-600/10 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-emerald-600/10 blur-3xl" />
      <div className="w-full max-w-md relative fade-up">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-11 h-11 rounded-xl bg-indigo-500 flex items-center justify-center"><Send size={20} className="text-white" /></div>
          <div>
            <div className="font-heading font-bold text-xl text-slate-50">SND Marketing</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-indigo-400">Customer data → campaigns</div>
          </div>
        </div>
        <div className="bg-[#111827] border border-slate-800 rounded-xl p-8">
          <h1 className="font-heading text-xl font-bold text-slate-50 mb-1">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h1>
          <p className="text-sm text-slate-400 mb-6">
            {mode === "login" ? "Sign in to manage your campaigns" : "Your own customer database, audiences and campaigns"}
          </p>
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label className="text-slate-300">Name</Label>
                <Input data-testid="auth-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="bg-slate-900 border-slate-700 text-slate-100" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-slate-300">Email</Label>
              <Input data-testid="auth-email-input" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@business.com" className="bg-slate-900 border-slate-700 text-slate-100" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">Password</Label>
              <Input data-testid="auth-password-input" required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" className="bg-slate-900 border-slate-700 text-slate-100" />
            </div>
            {error && <p data-testid="auth-error" className="text-sm text-rose-400">{error}</p>}
            <Button data-testid="auth-submit-button" type="submit" disabled={loading} className="w-full bg-indigo-500 hover:bg-indigo-600 text-white font-semibold">
              {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>
          <button
            data-testid="auth-switch-mode"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="mt-5 text-sm text-indigo-400 hover:text-indigo-300 w-full text-center"
          >
            {mode === "login" ? "New here? Create an account" : "Already have an account? Sign in"}
          </button>
        </div>
      </div>
    </div>
  );
}
